import React, { useCallback, useRef, useState } from 'react'
import { Upload, FileSpreadsheet, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { authFetch, getApiBase } from '../lib/api'

export function FileUpload({
  onUploadSuccess,
}: Readonly<{
  onUploadSuccess?: (batchId: number) => void
}>) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [status, setStatus] = useState<'idle' | 'uploading' | 'error'>('idle')
  const [uploadSummary, setUploadSummary] = useState<{
    totalRows: number
    processedCount: number
    missingUpc: number
    duplicateUpc: number
  } | null>(null)

  const resetFile = () => {
    setFile(null)
    setStatus('idle')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true)
    } else if (e.type === 'dragleave') {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files?.[0]) {
      validateAndSetFile(e.dataTransfer.files[0])
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      validateAndSetFile(e.target.files[0])
    }
  }

  const validateAndSetFile = (file: File) => {
    if (
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.endsWith('.xlsx')
    ) {
      setFile(file)
      setStatus('idle')
    } else {
      setStatus('error')
      toast.error('Please upload a valid Excel (.xlsx) file')
    }
  }

  const handleUpload = async () => {
    if (!file) return

    setStatus('uploading')
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await authFetch(`${getApiBase()}/api/products/upload`, {
        method: 'POST',
        body: formData,
      })

      const data = await response.json()

      if (response.ok) {
        const count: number = data.count ?? 0
        const missingUpc: number = data.missing_upc_count ?? 0
        const duplicateUpc: number = data.duplicate_upc_count ?? 0
        const totalRows: number = data.total_rows ?? count

        setUploadSummary({
          totalRows,
          processedCount: count,
          missingUpc,
          duplicateUpc,
        })

        let message = data.message || 'File uploaded successfully'
        if (count > 0) {
          const suffix = count === 1 ? '' : 's'
          message = `Successfully processed ${count} product${suffix}`
        }
        toast.success(message)
        resetFile()
        if (onUploadSuccess && typeof data.batch_id === 'number') {
          onUploadSuccess(data.batch_id)
        } else if (onUploadSuccess) {
          onUploadSuccess(Number.NaN)
        }
      } else {
        throw new Error(data.error || 'Upload failed')
      }
    } catch (error) {
      setStatus('error')
      toast.error(error instanceof Error ? error.message : 'Upload failed')
    }
  }

  return (
    <div className='w-full max-w-2xl mx-auto space-y-4'>
      <div
        className={`
          relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-200
          ${isDragging ? 'border-primary bg-primary/10 scale-[1.01] shadow-inner' : 'border-slate-300 hover:border-primary/50 hover:bg-slate-50/80'}
          ${status === 'error' ? 'border-destructive/50 bg-destructive/5' : ''}
        `}
      >
        <input
          type='file'
          accept='.xlsx'
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onChange={handleChange}
          ref={fileInputRef}
          className='absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10'
        />

        <div className='flex flex-col items-center justify-center space-y-4'>
          <div
            className={`
            p-5 rounded-2xl transition-colors
            ${status === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-gradient-to-br from-primary/15 to-primary/5 text-primary'}
          `}
          >
            {(() => {
              if (status === 'error') return <AlertCircle size={32} />
              if (file) return <FileSpreadsheet size={32} />
              return <Upload size={32} />
            })()}
          </div>

          <div className='space-y-1'>
            <h3 className='text-lg font-medium'>
              {file ? file.name : 'Drop your Excel file here'}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {file
                ? `${(file.size / 1024).toFixed(2)} KB`
                : 'or click to browse locally'}
            </p>
          </div>

          {file && (
            <div className='flex gap-3 z-20 pt-2'>
              <button
                onClick={e => {
                  e.stopPropagation()
                  resetFile()
                }}
                className='px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={e => {
                  e.stopPropagation()
                  handleUpload()
                }}
                className='px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2'
                disabled={status === 'uploading'}
              >
                {status === 'uploading' ? (
                  <>
                    <span className='animate-spin'>⏳</span> Uploading...
                  </>
                ) : (
                  'Upload File'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {uploadSummary && (
        <div className='rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4 text-sm text-slate-700'>
          <div className='font-semibold mb-2 text-slate-800'>
            Last upload summary
          </div>
          <dl className='grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-8'>
            <div className='flex items-center justify-between sm:justify-start sm:gap-2'>
              <dt className='text-xs uppercase tracking-wide text-slate-500'>
                Total rows
              </dt>
              <dd className='font-medium'>{uploadSummary.totalRows}</dd>
            </div>
            <div className='flex items-center justify-between sm:justify-start sm:gap-2'>
              <dt className='text-xs uppercase tracking-wide text-slate-500'>
                Imported
              </dt>
              <dd className='font-medium'>{uploadSummary.processedCount}</dd>
            </div>
            <div className='flex items-center justify-between sm:justify-start sm:gap-2'>
              <dt className='text-xs uppercase tracking-wide text-slate-500'>
                Missing UPC
              </dt>
              <dd className='font-medium'>
                {uploadSummary.missingUpc > 0 ? uploadSummary.missingUpc : '0'}
              </dd>
            </div>
            <div className='flex items-center justify-between sm:justify-start sm:gap-2'>
              <dt className='text-xs uppercase tracking-wide text-slate-500'>
                Duplicate UPC
              </dt>
              <dd className='font-medium'>
                {uploadSummary.duplicateUpc > 0
                  ? uploadSummary.duplicateUpc
                  : '0'}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  )
}
